// =============================================================================
// Module Project - Repository: Task Dependencies V2
// Standardized FS (Finish-to-Start) dependency model
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export type DependencyTypeV2 = 'FS'; // Future: 'SS' | 'FF' | 'SF'

export interface TaskDependencyV2 {
  id: string;
  org_id: string;
  project_id: string;
  predecessor_task_id: string;
  successor_task_id: string;
  dependency_type: DependencyTypeV2;
  created_by: string;
  created_at: string;
  // Hydrated relations
  predecessor_task?: {
    id: string;
    title: string;
    status_code: string;
  };
  successor_task?: {
    id: string;
    title: string;
    status_code: string;
  };
}

export interface TaskDependencyInfoV2 {
  taskId: string;
  isBlocked: boolean;
  hasDependencies: boolean;
  predecessorCount: number;
  successorCount: number;
  blockingTasks: Array<{ id: string; title: string; status_code: string }>;
}

export interface CreateDependencyInput {
  orgId: string;
  projectId: string;
  predecessorTaskId: string;
  successorTaskId: string;
  type?: DependencyTypeV2;
  createdBy: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// CYCLE DETECTION
// DFS from successor to check if adding this edge would create a cycle
// ─────────────────────────────────────────────────────────────────────────────

async function wouldCreateCycle(
  projectId: string,
  predecessorTaskId: string,
  successorTaskId: string
): Promise<boolean> {
  // If predecessor equals successor, it's already a direct cycle
  if (predecessorTaskId === successorTaskId) {
    return true;
  }

  // Get all existing dependencies for the project
  const { data: allDeps, error } = await supabase
    .from('project_task_dependencies_v2')
    .select('predecessor_task_id, successor_task_id')
    .eq('project_id', projectId);

  if (error || !allDeps) {
    console.error('Error fetching dependencies for cycle check:', error);
    return false; // Allow creation if we can't check
  }

  // Build adjacency list: predecessor -> successors[]
  const graph = new Map<string, string[]>();
  
  for (const dep of allDeps) {
    const existing = graph.get(dep.predecessor_task_id) || [];
    existing.push(dep.successor_task_id);
    graph.set(dep.predecessor_task_id, existing);
  }

  // Add the proposed edge
  const existingSuccessors = graph.get(predecessorTaskId) || [];
  existingSuccessors.push(successorTaskId);
  graph.set(predecessorTaskId, existingSuccessors);

  // DFS from successorTaskId to see if we can reach predecessorTaskId
  const visited = new Set<string>();
  const stack = [successorTaskId];

  while (stack.length > 0) {
    const current = stack.pop()!;
    
    if (current === predecessorTaskId) {
      return true; // Cycle detected!
    }

    if (visited.has(current)) {
      continue;
    }
    visited.add(current);

    const successors = graph.get(current) || [];
    for (const succ of successors) {
      if (!visited.has(succ)) {
        stack.push(succ);
      }
    }
  }

  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH PROJECT DEPENDENCIES
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchProjectDependenciesV2(
  projectId: string
): Promise<TaskDependencyV2[]> {
  const { data, error } = await supabase
    .from('project_task_dependencies_v2')
    .select(`
      *,
      predecessor_task:project_tasks!project_task_dependencies_v2_predecessor_task_id_fkey(
        id,
        title,
        status_code
      ),
      successor_task:project_tasks!project_task_dependencies_v2_successor_task_id_fkey(
        id,
        title,
        status_code
      )
    `)
    .eq('project_id', projectId);

  if (error) {
    console.warn('Project dependencies V2 fetch failed (non-blocking):', error.message);
    return [];
  }

  return (data || []).map((row: Record<string, unknown>) => ({
    id: row.id as string,
    org_id: row.org_id as string,
    project_id: row.project_id as string,
    predecessor_task_id: row.predecessor_task_id as string,
    successor_task_id: row.successor_task_id as string,
    dependency_type: row.dependency_type as DependencyTypeV2,
    created_by: row.created_by as string,
    created_at: row.created_at as string,
    predecessor_task: row.predecessor_task as TaskDependencyV2['predecessor_task'],
    successor_task: row.successor_task as TaskDependencyV2['successor_task'],
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH PREDECESSORS (tasks that block this task)
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchBlockingTasks(
  taskId: string
): Promise<Array<{ id: string; title: string; status_code: string; dependencyId: string }>> {
  const { data, error } = await supabase
    .from('project_task_dependencies_v2')
    .select(`
      id,
      predecessor_task:project_tasks!project_task_dependencies_v2_predecessor_task_id_fkey(
        id,
        title,
        status_code
      )
    `)
    .eq('successor_task_id', taskId);

  if (error) {
    console.error('Error fetching blocking tasks:', error);
    return [];
  }

  return (data || [])
    .filter((row: Record<string, unknown>) => row.predecessor_task)
    .map((row: Record<string, unknown>) => {
      const pred = row.predecessor_task as { id: string; title: string; status_code: string };
      return {
        id: pred.id,
        title: pred.title,
        status_code: pred.status_code,
        dependencyId: row.id as string,
      };
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH SUCCESSORS (tasks blocked by this task)
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchBlockedTasks(
  taskId: string
): Promise<Array<{ id: string; title: string; status_code: string; dependencyId: string }>> {
  const { data, error } = await supabase
    .from('project_task_dependencies_v2')
    .select(`
      id,
      successor_task:project_tasks!project_task_dependencies_v2_successor_task_id_fkey(
        id,
        title,
        status_code
      )
    `)
    .eq('predecessor_task_id', taskId);

  if (error) {
    console.error('Error fetching blocked tasks:', error);
    return [];
  }

  return (data || [])
    .filter((row: Record<string, unknown>) => row.successor_task)
    .map((row: Record<string, unknown>) => {
      const succ = row.successor_task as { id: string; title: string; status_code: string };
      return {
        id: succ.id,
        title: succ.title,
        status_code: succ.status_code,
        dependencyId: row.id as string,
      };
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// CHECK IF TASK IS BLOCKED
// A task is blocked if it has at least one predecessor with non-terminal status
// ─────────────────────────────────────────────────────────────────────────────

export async function isTaskBlockedV2(
  taskId: string,
  terminalStatusCodes: string[] = ['done', 'completed', 'closed', 'cancelled', 'archived']
): Promise<boolean> {
  const predecessors = await fetchBlockingTasks(taskId);
  
  // Check if any predecessor is NOT in a terminal status
  return predecessors.some(pred => !terminalStatusCodes.includes(pred.status_code));
}

// ─────────────────────────────────────────────────────────────────────────────
// GET BLOCKING REASONS (non-done predecessors)
// ─────────────────────────────────────────────────────────────────────────────

export async function getBlockingReasons(
  taskId: string,
  terminalStatusCodes: string[] = ['done', 'completed', 'closed', 'cancelled', 'archived']
): Promise<Array<{ id: string; title: string; status_code: string }>> {
  const predecessors = await fetchBlockingTasks(taskId);
  
  return predecessors
    .filter(pred => !terminalStatusCodes.includes(pred.status_code))
    .map(pred => ({
      id: pred.id,
      title: pred.title,
      status_code: pred.status_code,
    }));
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE DEPENDENCY
// ─────────────────────────────────────────────────────────────────────────────

export async function createDependencyV2(
  input: CreateDependencyInput
): Promise<{ success: boolean; dependency?: TaskDependencyV2; error?: string }> {
  const { orgId, projectId, predecessorTaskId, successorTaskId, type = 'FS', createdBy } = input;

  // Check for self-dependency
  if (predecessorTaskId === successorTaskId) {
    return { success: false, error: 'Une tâche ne peut pas dépendre d\'elle-même' };
  }

  // Check for cycle
  const hasCycle = await wouldCreateCycle(projectId, predecessorTaskId, successorTaskId);
  if (hasCycle) {
    return { 
      success: false, 
      error: 'Impossible : cette dépendance créerait une boucle (cycle)' 
    };
  }

  const { data, error } = await supabase
    .from('project_task_dependencies_v2')
    .insert({
      org_id: orgId,
      project_id: projectId,
      predecessor_task_id: predecessorTaskId,
      successor_task_id: successorTaskId,
      dependency_type: type,
      created_by: createdBy,
    })
    .select(`
      *,
      predecessor_task:project_tasks!project_task_dependencies_v2_predecessor_task_id_fkey(
        id,
        title,
        status_code
      ),
      successor_task:project_tasks!project_task_dependencies_v2_successor_task_id_fkey(
        id,
        title,
        status_code
      )
    `)
    .single();

  if (error) {
    console.error('Error creating dependency V2:', error);
    if (error.code === '23505') { // Unique constraint violation
      return { success: false, error: 'Cette dépendance existe déjà' };
    }
    return { success: false, error: 'Erreur lors de la création de la dépendance' };
  }

  const row = data as Record<string, unknown>;
  const dependency: TaskDependencyV2 = {
    id: row.id as string,
    org_id: row.org_id as string,
    project_id: row.project_id as string,
    predecessor_task_id: row.predecessor_task_id as string,
    successor_task_id: row.successor_task_id as string,
    dependency_type: row.dependency_type as DependencyTypeV2,
    created_by: row.created_by as string,
    created_at: row.created_at as string,
    predecessor_task: row.predecessor_task as TaskDependencyV2['predecessor_task'],
    successor_task: row.successor_task as TaskDependencyV2['successor_task'],
  };

  return { success: true, dependency };
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE DEPENDENCY
// ─────────────────────────────────────────────────────────────────────────────

export async function deleteDependencyV2(dependencyId: string): Promise<boolean> {
  const { error } = await supabase
    .from('project_task_dependencies_v2')
    .delete()
    .eq('id', dependencyId);

  if (error) {
    console.error('Error deleting dependency V2:', error);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH DEPENDENCY INFO FOR MULTIPLE TASKS (batch for Kanban/List)
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchTasksDependencyInfoV2(
  taskIds: string[],
  terminalStatusCodes: string[] = ['done', 'completed', 'closed', 'cancelled', 'archived']
): Promise<Map<string, TaskDependencyInfoV2>> {
  // Filter out optimistic/temp IDs that aren't valid UUIDs to avoid 400s.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  taskIds = taskIds.filter((id) => UUID_RE.test(id));
  if (taskIds.length === 0) {
    return new Map();
  }

  // Get all dependencies where any of these tasks is predecessor or successor
  const { data, error } = await supabase
    .from('project_task_dependencies_v2')
    .select(`
      predecessor_task_id,
      successor_task_id,
      predecessor_task:project_tasks!project_task_dependencies_v2_predecessor_task_id_fkey(
        id,
        title,
        status_code
      )
    `)
    .or(`predecessor_task_id.in.(${taskIds.join(',')}),successor_task_id.in.(${taskIds.join(',')})`);

  if (error) {
    console.warn('Tasks dependency info V2 fetch failed (non-blocking):', error.message);
    return new Map();
  }

  // Initialize result map
  const result = new Map<string, TaskDependencyInfoV2>();
  
  for (const taskId of taskIds) {
    result.set(taskId, {
      taskId,
      isBlocked: false,
      hasDependencies: false,
      predecessorCount: 0,
      successorCount: 0,
      blockingTasks: [],
    });
  }

  // Process dependencies
  for (const row of (data || [])) {
    const predId = row.predecessor_task_id;
    const succId = row.successor_task_id;
    const predTask = row.predecessor_task as { id: string; title: string; status_code: string } | null;

    // Update predecessor info (this task blocks something)
    if (taskIds.includes(predId)) {
      const info = result.get(predId)!;
      info.hasDependencies = true;
      info.successorCount++;
    }

    // Update successor info (this task is blocked by something)
    if (taskIds.includes(succId)) {
      const info = result.get(succId)!;
      info.hasDependencies = true;
      info.predecessorCount++;

      // Check if predecessor is non-terminal -> this task is blocked
      if (predTask && !terminalStatusCodes.includes(predTask.status_code)) {
        info.isBlocked = true;
        info.blockingTasks.push({
          id: predTask.id,
          title: predTask.title,
          status_code: predTask.status_code,
        });
      }
    }
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// SEARCH TASKS IN PROJECT (for dependency picker)
// ─────────────────────────────────────────────────────────────────────────────

export async function searchProjectTasksV2(
  projectId: string,
  excludeTaskId: string,
  searchTerm?: string
): Promise<Array<{ id: string; title: string; status_code: string }>> {
  let query = supabase
    .from('project_tasks')
    .select('id, title, status_code')
    .eq('project_id', projectId)
    .neq('id', excludeTaskId)
    .is('parent_task_id', null) // Only main tasks
    .order('created_at', { ascending: false })
    .limit(20);

  if (searchTerm && searchTerm.trim()) {
    query = query.ilike('title', `%${searchTerm.trim()}%`);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error searching tasks V2:', error);
    return [];
  }

  return data || [];
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH GANTT ARROWS (for Gantt view)
// Returns links from predecessor to successor
// ─────────────────────────────────────────────────────────────────────────────

export interface GanttDependencyLink {
  fromTaskId: string;
  toTaskId: string;
  type: DependencyTypeV2;
}

export async function fetchGanttDependencies(
  projectId: string
): Promise<GanttDependencyLink[]> {
  const { data, error } = await supabase
    .from('project_task_dependencies_v2')
    .select('predecessor_task_id, successor_task_id, dependency_type')
    .eq('project_id', projectId);

  if (error) {
    console.warn('Gantt dependencies fetch failed (non-blocking):', error.message);
    return [];
  }

  return (data || []).map((d: Record<string, unknown>) => ({
    fromTaskId: d.predecessor_task_id as string,
    toTaskId: d.successor_task_id as string,
    type: d.dependency_type as DependencyTypeV2,
  }));
}
